import { useCallback, useRef, useState } from 'react';

import {
  type DocumentDraft,
  type DocumentDraftContent,
  uploadDocumentAttachment,
} from '@/entities/document-draft';

export function useDocumentAttachments(
  patientId: string,
  draft: DocumentDraft | null,
  updateDraft: (patch: Partial<DocumentDraftContent>) => void
) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestDraftRef = useRef(draft);
  latestDraftRef.current = draft;

  const upload = useCallback(
    async (file: File) => {
      setUploading(true);
      setError(null);
      try {
        const attachment = await uploadDocumentAttachment(patientId, file);
        const latestDraft = latestDraftRef.current;
        if (latestDraft !== null) {
          updateDraft({ attachments: [...latestDraft.content.attachments, attachment] });
        }
      } catch (uploadError) {
        setError(uploadError instanceof Error ? uploadError.message : 'Attachment upload failed.');
      } finally {
        setUploading(false);
      }
    },
    [patientId, updateDraft]
  );

  const remove = useCallback(
    (attachmentId: string) => {
      if (draft === null) {
        return;
      }
      updateDraft({
        attachments: draft.content.attachments.filter(
          (attachment) => attachment.attachmentId !== attachmentId
        ),
      });
    },
    [draft, updateDraft]
  );

  return { upload, remove, error, uploading };
}
